import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateTime as LuxonDateTime } from 'luxon';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';
import { InquiryMessage, InquiryRow } from 'src/models/inquiry.model';
import { InquiryStatusTone, inquiryStatusPresentation } from 'src/shared/util/inquiry-status';

const PLATFORM_ICONS: Record<string, string> = {
  airbnb: 'assets/img/airbnb-icon.svg'
};

/**
 * The booking-inquiry object that sits in a guest message thread, the way
 * Airbnb shows a "Booking Inquiry" card under the guest's first message.
 * Purely presentational: tapping it asks the page to open the inquiry.
 *
 * Status label/tone come from the shared inquiry-status helper so the card,
 * the Inquiries list and the detail page never disagree about a lifecycle.
 */
@Component({
  selector: 'app-inquiry-card',
  templateUrl: 'inquiry-card.component.html',
  styleUrls: ['inquiry-card.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InquiryCardComponent {
  @Input() message: InquiryMessage;
  @Output() openInquiry = new EventEmitter<InquiryRow>();

  get inquiry(): InquiryRow | null {
    return this.message?.content?.inquiry || null;
  }

  get statusLabel(): string {
    return this.inquiry ? inquiryStatusPresentation(this.lifecycle).label : '';
  }

  get tone(): InquiryStatusTone {
    return this.inquiry ? inquiryStatusPresentation(this.lifecycle).tone : 'neutral';
  }

  get platformIcon(): string {
    return PLATFORM_ICONS[this.inquiry?.platform] || 'assets/svg/chatbubble-outline.svg';
  }

  /** "Oct 9 – 14, 2026" within a month, "Oct 30 – Nov 2, 2026" across months; '' without firm dates. */
  get dateRange(): string {
    const checkIn = this.parseDate(this.inquiry?.check_in_date);
    const checkOut = this.parseDate(this.inquiry?.check_out_date);
    if (!checkIn || !checkOut) {
      return '';
    }
    const sameMonth = checkIn.hasSame(checkOut, 'month') && checkIn.hasSame(checkOut, 'year');
    const start = checkIn.toFormat('LLL d');
    const end = sameMonth ? checkOut.toFormat('d, yyyy') : checkOut.toFormat('LLL d, yyyy');
    return `${start} – ${end}`;
  }

  get nights(): number {
    const checkIn = this.parseDate(this.inquiry?.check_in_date);
    const checkOut = this.parseDate(this.inquiry?.check_out_date);
    if (!checkIn || !checkOut) {
      return 0;
    }
    return Math.max(0, Math.round(checkOut.diff(checkIn, 'days').days));
  }

  get stayLabel(): string {
    const range = this.dateRange;
    if (!range) {
      return 'Dates not set';
    }
    const nights = this.nights;
    return `${range} · ${nights === 1 ? '1 night' : `${nights} nights`}`;
  }

  get guestsLabel(): string {
    const guests = this.inquiry?.total_guests ?? 0;
    const pets = this.inquiry?.total_pets ?? 0;
    const label = guests === 1 ? '1 guest' : `${guests} guests`;
    if (!pets) {
      return label;
    }
    return `${label}, ${pets === 1 ? '1 pet' : `${pets} pets`}`;
  }

  /** Host payout for a channel inquiry, the charge total for a direct one; '' when the channel sent no money. */
  get amountLabel(): string {
    const cents = this.amountCents;
    if (cents === null) {
      return '';
    }
    const currency = this.inquiry?.currency || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  get amountCaption(): string {
    return this.inquiry?.platform === 'tidy' ? 'Stay total' : 'Host payout';
  }

  get isAwaitingResponse(): boolean {
    return this.inquiry?.status === 'pending';
  }

  /** "Sep 21, 6:34 PM" in the viewer's timezone; '' once answered or resolved. */
  get deadlineLabel(): string {
    const deadline = this.inquiry?.response_deadline;
    if (!deadline || !this.isAwaitingResponse) {
      return '';
    }
    const dateTime = LuxonDateTime.fromISO(deadline).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    return dateTime.isValid ? dateTime.toFormat('LLL d, h:mm a') : '';
  }

  get isDeadlinePassed(): boolean {
    const deadline = this.inquiry?.response_deadline;
    return !!deadline && this.isAwaitingResponse && LuxonDateTime.fromISO(deadline) < LuxonDateTime.now();
  }

  onOpen(): void {
    if (this.inquiry) {
      this.openInquiry.emit(this.inquiry);
    }
  }

  private get lifecycle() {
    return {
      status: this.inquiry.status,
      platform: this.inquiry.platform,
      available_actions: this.inquiry.available_actions || [],
      expires_at: this.inquiry.expires_at,
      check_in_date: this.inquiry.check_in_date
    };
  }

  private get amountCents(): number | null {
    const inquiry = this.inquiry;
    if (!inquiry) {
      return null;
    }
    if (inquiry.platform === 'tidy') {
      return inquiry.total_amount ?? null;
    }
    const lines = inquiry.amounts_breakdown || [];
    if (!lines.length) {
      return null;
    }
    return lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  }

  private parseDate(value: string | null | undefined): LuxonDateTime | null {
    if (!value) {
      return null;
    }
    const dateTime = LuxonDateTime.fromISO(value);
    return dateTime.isValid ? dateTime : null;
  }
}
